import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Image as ImageIcon, Loader2, Paperclip, Send, XCircle } from "lucide-react";

import { api, uploadFile } from "@/lib/api";
import { Header } from "@/components/header";
import { Button, Field } from "@/components/kit";
import { useToast } from "@/components/toast";
import { SERVICE_TYPES } from "@/lib/format";

const FIELDS = [
  { key: "client_last_name", label: "Nom du client *", ph: "Martin" },
  { key: "client_first_name", label: "Prénom du client *", ph: "Paul" },
  { key: "client_phone", label: "Téléphone *", ph: "06 12 34 56 78", type: "tel" },
  { key: "client_email", label: "E-mail du client", ph: "paul.martin@mail.fr", type: "email" },
  { key: "address", label: "Adresse du projet *", ph: "12 rue des Lilas" },
  { key: "postal_code", label: "Code postal *", ph: "69000", im: "numeric" },
  { key: "city", label: "Ville *", ph: "Lyon" },
];
const EMPTY = { client_last_name: "", client_first_name: "", client_phone: "", client_email: "", address: "", postal_code: "", city: "", budget: "", description: "" };
const DOC_TYPES = "application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function Choice({ label, active, onClick, testID }) {
  return (
    <button
      type="button"
      data-testid={testID}
      aria-pressed={active}
      onClick={onClick}
      className={`focus-ring h-10 shrink-0 rounded-full border px-4 text-sm font-medium transition-colors ${active ? "border-[#D4AF37] bg-[#292415] text-[#D4AF37]" : "border-[#333333] bg-[#2A2A2A] text-[#CCCCCC] hover:border-[#555555]"}`}
    >
      {label}
    </button>
  );
}

function FilesPicker({ files, setFiles, uploading, setUploading }) {
  const toast = useToast();
  const photos = useRef(null);
  const docs = useRef(null);
  const add = async (list) => {
    if (!list?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(list)) {
        const up = await uploadFile(f);
        setFiles((p) => [...p, up]);
      }
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <span className="t-label">Documents ou photos</span>
      <input ref={photos} type="file" accept="image/*" multiple hidden data-testid="recommend-photos-file-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      <input ref={docs} type="file" accept={DOC_TYPES} multiple hidden data-testid="recommend-docs-file-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      <div className="flex gap-2">
        <Button testID="recommend-add-photos-button" title="Photos" icon={ImageIcon} variant="secondary" className="flex-1" onClick={() => photos.current?.click()} disabled={uploading} />
        <Button testID="recommend-add-docs-button" title="Documents" icon={Paperclip} variant="secondary" className="flex-1" onClick={() => docs.current?.click()} disabled={uploading} />
      </div>
      {uploading && (
        <p className="flex items-center gap-2 text-sm"><Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" /> Envoi en cours…</p>
      )}
      {files.map((f) => {
        const Icon = f.content_type.startsWith("image") ? ImageIcon : FileText;
        return (
          <div key={f.id} data-testid={`recommend-file-${f.id}`} className="flex items-center gap-2 rounded-xl bg-[#1E1E1E] p-3">
            <Icon className="h-5 w-5 text-[#D4AF37]" aria-hidden />
            <span className="t-body flex-1 truncate">{f.name}</span>
            <button type="button" aria-label="Retirer le fichier" data-testid={`recommend-remove-file-${f.id}`} onClick={() => setFiles((p) => p.filter((x) => x.id !== f.id))} className="focus-ring rounded-full text-[#9E9E9E] hover:text-white">
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function Recommend() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [projectType, setProjectType] = useState("");
  const [service, setService] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (t) => {
    setForm((p) => ({ ...p, [k]: t }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const services = projectType
    ? SERVICE_TYPES[projectType]
    : [...SERVICE_TYPES.construction, ...SERVICE_TYPES.renovation.filter((x) => !SERVICE_TYPES.construction.includes(x))];

  const submit = async () => {
    const errs = {};
    FIELDS.forEach((f) => {
      if (f.label.includes("*") && !form[f.key].trim()) errs[f.key] = "Champ obligatoire";
    });
    if (form.client_email && !/^\S+@\S+\.\S+$/.test(form.client_email.trim())) errs.client_email = "E-mail invalide";
    if (!projectType) errs.project_type = "Choisissez un type de projet";
    if (!service) errs.service_type = "Choisissez une prestation";
    if (!form.description.trim()) errs.description = "Décrivez le projet";
    if (form.budget && isNaN(Number(form.budget.replace(/\s/g, "").replace(",", ".")))) errs.budget = "Montant invalide";
    setErrors(errs);
    setError("");
    if (Object.keys(errs).length) return setError("Merci de compléter les champs indiqués.");
    setLoading(true);
    try {
      const rec = await api("/recommendations", {
        method: "POST",
        body: {
          ...form,
          client_email: form.client_email.trim() || null,
          budget: form.budget ? Number(form.budget.replace(/\s/g, "").replace(",", ".")) : null,
          project_type: projectType,
          service_type: service,
          file_ids: files.map((f) => f.id),
        },
      });
      qc.invalidateQueries();
      toast(`Recommandation envoyée · dossier ${rec.number}`);
      navigate(`/dossier/${rec.id}`, { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (f) => (
    <Field key={f.key} testID={`recommend-${f.key.replace(/_/g, "-")}-input`} label={f.label} placeholder={f.ph} value={form[f.key]} onChangeText={set(f.key)} error={errors[f.key]} type={f.type ?? "text"} inputMode={f.im} />
  );

  return (
    <div data-testid="recommend-screen" className="flex min-h-full flex-col">
      <Header title="Recommander un client" subtitle="Le dossier vous sera automatiquement associé" />
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate className="mx-auto grid w-full max-w-5xl gap-6 p-4 pb-40 md:p-8 md:pb-32 lg:grid-cols-2">
        <section className="card flex flex-col gap-4">
          <h2 className="t-h3 !text-[#D4AF37]">Le client</h2>
          {FIELDS.slice(0, 4).map(field)}
        </section>
        <section className="card flex flex-col gap-4">
          <h2 className="t-h3 !text-[#D4AF37]">Le projet</h2>
          {FIELDS.slice(4).map(field)}
        </section>
        <section className="card flex flex-col gap-4 lg:col-span-2">
          <div className="flex flex-col gap-2">
            <span className="t-label">Type de projet *</span>
            <div className="flex gap-2">
              {["construction", "renovation"].map((pt) => (
                <Choice key={pt} testID={`recommend-type-${pt}`} label={pt === "construction" ? "Construction neuve" : "Rénovation"} active={projectType === pt}
                  onClick={() => {
                    setProjectType(pt);
                    if (service && !SERVICE_TYPES[pt].includes(service)) setService("");
                    setErrors((e) => ({ ...e, project_type: undefined }));
                  }} />
              ))}
            </div>
            {errors.project_type ? <span className="t-caption !text-[#EB5757]">{errors.project_type}</span> : null}
          </div>
          <div className="flex flex-col gap-2">
            <span className="t-label">Type de prestation recherchée *</span>
            <div className="flex flex-wrap gap-2">
              {services.map((sv, i) => (
                <Choice key={sv} testID={`recommend-service-${i}`} label={sv} active={service === sv} onClick={() => { setService(sv); setErrors((e) => ({ ...e, service_type: undefined })); }} />
              ))}
            </div>
            {errors.service_type ? <span className="t-caption !text-[#EB5757]">{errors.service_type}</span> : null}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field testID="recommend-budget-input" label="Budget estimatif (€)" placeholder="250000" inputMode="decimal" value={form.budget} onChangeText={set("budget")} error={errors.budget} />
          </div>
          <Field testID="recommend-description-input" label="Description complète du projet *" placeholder="Terrain, surface souhaitée, délais, attentes du client…" multiline value={form.description} onChangeText={set("description")} error={errors.description} />
          <FilesPicker files={files} setFiles={setFiles} uploading={uploading} setUploading={setUploading} />
          {error ? <p data-testid="recommend-error" role="alert" className="whitespace-pre-line text-sm text-[#EB5757]">{error}</p> : null}
        </section>
        <div className="fixed inset-x-0 bottom-16 z-20 border-t border-[#222222] bg-[#121212]/95 p-4 backdrop-blur-md lg:bottom-0 lg:left-72">
          <div className="mx-auto max-w-5xl">
            <Button testID="recommend-submit-button" type="submit" title="ENVOYER LA RECOMMANDATION" icon={Send} size="lg" loading={loading} disabled={uploading} className="w-full lg:ml-auto lg:flex lg:w-auto lg:px-10" />
          </div>
        </div>
      </form>
    </div>
  );
}
